// frontend/src/components/ModelSelector.js
import React, { useState, useEffect } from "react";
import { modelAPI } from "../utils/api";

const ModelSelector = ({
  user,
  selectedModel,
  onModelChange,
  onModelStatsUpdate,
}) => {
  const [availableModels, setAvailableModels] = useState([]);
  const [modelStats, setModelStats] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchAvailableModels = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const response = await modelAPI.getAvailableModels();

      if (response.data.success) {
        setAvailableModels(response.data.models || []);
        setModelStats(response.data.modelStats || {});

        if (onModelStatsUpdate) {
          onModelStatsUpdate(response.data.modelStats);
        }
      }
    } catch (error) {
      console.error("Error fetching models:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateModel = async (modelId) => {
    if (!user) return;

    try {
      const response = await modelAPI.updateModel(modelId);

      if (response.data.success) {
        onModelChange(modelId);
        fetchAvailableModels();
      }
    } catch (error) {
      console.error("Error updating model:", error);
    }
  };

  useEffect(() => {
    fetchAvailableModels();
  }, [user]);

  if (!user) return null;

  return (
    <div className="model-selector">
      <div className="flex items-center justify-between mb-1.5 md:mb-2">
        <label className="text-xs md:text-sm font-medium text-text-secondary">
          AI Model Selection
        </label>
        <button
          onClick={fetchAvailableModels}
          className="text-xs text-primary hover:text-primary-hover"
          disabled={loading}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {loading ? (
        <div className="text-xs md:text-sm text-text-muted">Loading models...</div>
      ) : availableModels.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 md:gap-2">
          {availableModels.map((model) => (
            <button
              key={model.id}
              onClick={() => updateModel(model.id)}
              className={`px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm rounded-lg border transition-all flex flex-col items-center justify-center min-w-[70px] md:min-w-[80px] ${
                selectedModel === model.id
                  ? "bg-primary text-white border-primary shadow-md"
                  : "bg-surface border-border hover:bg-surface-elevated hover:border-primary"
              } ${!model.available ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={!model.available || selectedModel === model.id}
              title={`${model.description}\nUsed: ${model.usedToday}/${model.dailyLimit}\nRemaining: ${model.remaining}`}
            >
              <div className="font-medium truncate w-full text-center">
                {model.name.split(' ')[0]}
              </div>
              <div className="text-[10px] md:text-xs opacity-80 mt-0.5 md:mt-1 text-center">
                {model.remaining}/{model.dailyLimit}
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="model-unavailable-message">
          <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
            <span className="text-warning">⚠️</span>
            <span className="text-xs md:text-sm font-medium">All Models at Capacity</span>
          </div>
          <p className="text-xs text-text-muted mb-1.5 md:mb-2">
            All AI models have reached their daily usage limits.
          </p>
          <div className="text-[10px] md:text-xs text-text-muted space-y-0.5 md:space-y-1">
            <p>• Limits reset daily at midnight UTC</p>
            <p>• Try again tomorrow</p>
            <p>• Consider upgrading for higher limits</p>
          </div>
        </div>
      )}

      {selectedModel && modelStats[selectedModel] && (
        <div className="mt-2 md:mt-3 text-[10px] md:text-xs text-text-muted">
          <div className="flex items-center justify-between">
            <span className="truncate">Current: {modelStats[selectedModel]?.displayName}</span>
            <span className="whitespace-nowrap">
              Used: {modelStats[selectedModel]?.usedToday || 0}/
              {modelStats[selectedModel]?.dailyLimit || 0}
            </span>
          </div>
          <div className="w-full bg-border h-1 md:h-1.5 rounded-full mt-0.5 md:mt-1 overflow-hidden">
            <div
              className="bg-primary h-full rounded-full transition-all duration-500"
              style={{
                width: `${Math.min(
                  100,
                  ((modelStats[selectedModel]?.usedToday || 0) /
                    (modelStats[selectedModel]?.dailyLimit || 1)) *
                    100
                )}%`,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelSelector;